// @flow
import { PDFFillerContext } from "pages/Contexts/PdfFillerContext";
import * as React from "react";
import styled from "styled-components";
import type { InputField } from "types/input-field";

import { TriggerUserActions } from "@/components/TriggerUserActions";

const StyledInputWrapper = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
`;

const StyledInput = styled.textarea`
  width: 100%;
  height: 100%;
  flex: 1;
  font-size: ${(props: { $fontSize?: number }) =>
    props.$fontSize ? `${props.$fontSize}pt` : "inherit"};
  background: transparent;
  outline: none;
  resize: none;
  overflow-y: hidden;

  &:focus {
    outline: none;
  }

  &[type="checkbox"] {
    height: 100%;
  }
`;

export const StyledPlaceholder: React.ComponentType<{}> = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  background: transparent;
`;

export const FakeInput: React.ComponentType<{}> = styled.div`
  background: transparent;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

export type Props = {
  disabled?: boolean,
  fontSize?: number,
  innerRef: any,
  inputField: InputField,
  onChange: (event: SyntheticEvent<HTMLInputElement>) => void | Promise<void>,
  onFocus: () => void,
  setVariableHeight?: (height: number) => void,
  value?: ?string,
  variableHeight?: number,
  placeholder?: string,
  viewValue?: string,
};

function TextInput({
  disabled,
  fontSize,
  innerRef,
  inputField,
  onChange,
  onFocus,
  setVariableHeight,
  value,
  variableHeight,
  placeholder,
  viewValue,
  ...props
}: Props): React.Node {
  const [isFocused, setIsFocused] = React.useState(false);

  const { answers, viewMode, zoom } = React.useContext(PDFFillerContext);

  const resizeInput = React.useCallback(() => {
    if (inputField.input_type !== "text") return;
    if (!innerRef.current) return;
    if (!setVariableHeight) return;
    if (viewMode === "edit") return;

    const height = innerRef.current.scrollHeight / zoom;
    setVariableHeight(height);
  }, [innerRef, inputField.input_type, setVariableHeight, viewMode, zoom]);

  const handleInput = React.useCallback(() => {
    resizeInput();
  }, [resizeInput]);

  const handleFocus = React.useCallback(() => {
    setIsFocused(true);
    onFocus();
  }, [onFocus]);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
  }, []);

  const handlePlaceholderClick = React.useCallback(() => {
    if (innerRef.current) innerRef.current.focus();
  }, [innerRef]);

  React.useEffect(() => {
    resizeInput();
  }, [resizeInput, variableHeight]);

  if (viewMode === "edit") {
    return (
      <FakeInput>
        <span>
          {inputField.input_type === "date"
            ? "mm/dd/yyyy"
            : inputField?.placeholder || viewValue || "Text"}
        </span>
      </FakeInput>
    );
  }

  const inputValue = value || answers[inputField.id]?.answer_raw || "";

  const showPlaceholder = viewMode !== "response" && !inputValue && !isFocused;

  return (
    <StyledInputWrapper>
      {showPlaceholder ? (
        <StyledPlaceholder
          className="pl-2 pt-1"
          onClick={handlePlaceholderClick}
        >
          {inputField?.placeholder || placeholder || "Text"}
        </StyledPlaceholder>
      ) : null}
      <TriggerUserActions>
        <StyledInput
          $fontSize={fontSize}
          data-type="document-input"
          disabled={disabled}
          onBlur={handleBlur}
          onChange={onChange}
          onFocus={handleFocus}
          onInput={handleInput}
          ref={innerRef}
          type={inputField.input_type}
          value={inputValue}
          {...props}
        />
      </TriggerUserActions>
    </StyledInputWrapper>
  );
}

export default TextInput;
